import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSPanelProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { ListSyncActivitiesSortParameter } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData
} from "@query";
import { selectorQuickbooksConnection } from "../../../modules/Integrations/selectors/selectorQuickbooksConnection";
import { QuickbooksSyncBadge } from "../../../Settings/components/QuickbooksSyncBadge/QuickbooksSyncBadge";

/**
 * The QuickBooks sync history for one record (invoice, payable, client or
 * contractor), on v3 `listSyncActivities`.
 *
 * v1 read `/integrations/quickbooks/service/sync/activity` with an `entityId`
 * filter. v3 scopes activities to a connection, so the connection is resolved
 * first (`listAccountingConnections` → [[selectorQuickbooksConnection]]) and the
 * per-record filter is `filter.entityId.eq`.
 *
 * Accepted breakage: v3's `SyncActivity` model carries no timestamp — the
 * collection can be *sorted* by `createdAt` but does not project it — so the
 * per-entry date v1 printed under each line is gone. Entries are still ordered
 * newest-first, which is what the badge and the ordering depend on.
 * See V3_MIGRATION_TRACKER.md · /v3/platform/accounting-connections.
 */
export const QuickbooksHistory: React.FC<
  {
    entityId: string;
    Wrapper: React.FC;
  } & WSElementProps &
    WSPanelProps
> = ({ entityId, Wrapper, ...props }) => {
  const history = useHistory();

  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connectionId = selectorQuickbooksConnection(queryConnections.data)?.id;

  const querySyncActivities =
    queriesAccountingConnections.listSyncActivities.useQuery(
      {
        connectionId: connectionId as string,
        filter: { entityId: { eq: entityId } },
        sort: ListSyncActivitiesSortParameter.CREATED_AT2,
        page: { size: 100 }
      },
      { enabled: !!connectionId && !!entityId }
    );

  // The panel is supplementary: a missing connection or a failed read leaves it
  // out entirely rather than rendering an error into the host screen.
  const activities = querySyncActivities.data?.data ?? [];

  if (querySyncActivities.isError || !activities.length) {
    return null;
  }

  return (
    <Wrapper {...props} data-testid="qboHistory">
      <WSFlexBox
        wrap="nowrap"
        alignItems="center"
        justify="space-between"
        mb="XL"
      >
        <WSText.Heading5>QBO history</WSText.Heading5>
        <QuickbooksSyncBadge latestActivity={activities[0]} />
      </WSFlexBox>
      {activities.map((activity, index) => (
        <WSElement
          mb={index === activities.length - 1 ? undefined : "S"}
          key={activity.id}
        >
          <WSText weight="medium" color="gray600">
            {`${
              activity.isError ? `Failed to ` : `Successfully `
            }${activity.action.toLocaleLowerCase()}${
              activity.isError ? `` : `d`
            } ${activity.displayName ?? activity.entityType}${
              activity.message === "MarkedAsPaid" ? ", marked as paid." : ""
            }`}
          </WSText>
        </WSElement>
      ))}
      <WSButton.Link
        mt="XL"
        rightIcon="arrow-right"
        onClick={() => {
          history.push({
            pathname:
              "/member/settings/integrations/quickbooks/history/" +
              activities[0].entityId,
            state: {
              backPath: history.location.pathname
            }
          });
        }}
      >
        View sync details
      </WSButton.Link>
    </Wrapper>
  );
};
